import React, { useState } from "react";
import { Grid, Card, Typography, Box, Button } from "@mui/material";
import { Container, styled } from "@mui/system";
import { useNavigate } from "react-router-dom"; // For navigation

import ServiceImg1 from '../images/Defence.jpg';
import ServiceImg2 from '../images/Sourcing.jpg';
import ServiceImg3 from '../images/Supply.jpg';
import ServiceImg4 from '../images/QualityAssurance.jpg';

import DomainAddIcon from "@mui/icons-material/DomainAdd";
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

// Service data with descriptions and routes
const serviceData = [
  {
    id: 1,
    background: ServiceImg1,
    title: "Defence Contract Procurement",
    description: "Government defence contracts (DGDP, CMTD, DGMS, MES, Army Aviation), APC, MRAP, arms, ammunition, and aircraft spare parts",
    
  },
  {
    id: 2,
    background: ServiceImg2,
    title: "Global Product Sourcing",
    description: "OEM-certified equipment sourcing across multiple jurisdictions with complete quality assurance verification",
    
  },
  {
    id: 3,
    background: ServiceImg3,
    title: "Supply Chain Management",
    description: "End-to-end supply chain coordination, documentation, and delivery management",
    
  },
  {
    id: 4,
    background: ServiceImg4,
    title: "Quality Assurance & Compliance",
    description: "Comprehensive compliance verification and quality control protocols for all transactions",
    
  },
];

// Dark Overlay for hover effect
const DarkOverlay = styled(Box)(({ isVisible }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  opacity: isVisible ? 1 : 0,
  transition: "opacity 0.3s ease",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: "20px",
  zIndex: 2,
  pointerEvents: "none", // Allows clicking through to the card
}));

// Description text overlay
const DescriptionText = styled(Typography)(({ isVisible }) => ({
  color: "#ffffff",
  textAlign: "center",
  padding: "10px",
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? "translateY(0)" : "translateY(10px)",
  transition: "all 0.3s ease 0.1s", // Slight delay for staggered animation
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: 1.5,
}));

// Styled Card Component
const StyledCard = styled(Card)(({ isExpanded, background }) => ({
  position: isExpanded ? "absolute" : "relative",
  zIndex: isExpanded ? 10 : 1,
  width: isExpanded ? "122%" : "95%",
  height: "100%",
  left: isExpanded ? "-16%" : "0",
  transition: "all 0.5s ease",
  transitionDuration: "500ms",
  transitionProperty: "all, margin-right",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  textAlign: "center",
  backgroundImage: `url(${background})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "#ffffff",
  cursor: "pointer",
  overflow: "hidden",
  marginRight: isExpanded ? "20px" : "0",
  marginLeft: isExpanded ? "10px" : "0",
  "&:hover": {
    "& .dark-overlay": {
      opacity: 1,
    },
    "& .description-text": {
      opacity: 1,
      transform: "translateY(0)",
    }
  }
}));

const CardContent = styled(Box)(() => ({
  position: "relative",
  zIndex: 3,
  padding: "1",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  textAlign: "center",
  height: "100%",
  width: "100%",
}));

const StyledButton = styled(Button)(({ isExpanded }) => ({
  marginTop: "auto",
  padding: "10px 12px",
  color: isExpanded ? "#000" : "#000",
  backgroundColor: isExpanded ? "#fff" : "rgba(255, 255, 255, 0.9)",
  width: isExpanded ? "300px" : "210px",
  height: isExpanded ? "100px" : "50px",
  fontWeight: "bold",
  marginLeft: isExpanded ? "-55px" : "-148px",
  marginBottom: isExpanded ? "0" : "42px",
  textOrientation: "upright",
  transform: isExpanded ? "rotate(0deg)" : "rotate(270deg)",
  transition: "transform 0.5s ease, background-color 0.5s ease, color 0.5s ease",
  zIndex: 4,
}));

const FourColumnHover = () => {
  const [activeCardId, setActiveCardId] = useState(1);
  const navigate = useNavigate();

  const handleCardClick = (link) => {
    navigate(link);
  };

  const handleCardMouseEnter = (cardId) => {
    setActiveCardId(cardId);
  };

  const handleCardMouseLeave = () => {
    setActiveCardId(1);
  };

  return (
    <>
      <div id="services-section">
        <Box sx={{ px: { xs: 6, sm: 4, md: 6 }, py: { xs: 4, sm: 6 } }}>
          <Grid
            container
            spacing={8}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={12} md="auto">
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{ fontSize: { xs: "24px", sm: "32px", md: "40px" } }}
              >
                Core Services
              </Typography>
            </Grid>

            <Grid item xs={12} md="auto" textAlign={{ xs: "center", md: "right" }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#0F758E",
                  color: "white",
                  fontSize: { xs: "12px", sm: "14px" },
                  "&:hover": {
                    backgroundColor: "white",
                    color: "#0F758E",
                    border: "2px solid #0F758E",
                  },
                }}
              >
                Services
              </Button>
            </Grid>
          </Grid>
        </Box>


        <Container
          sx={{
            height: "90vh",
            mr: 10,
            ml: 6,
          }}
        >
          <Grid
            container
            spacing={0}
            justifyContent="center"
            sx={{
              height: "90%",
              width: "88vw",
              mx: "auto",
            }}
          >
            {serviceData.map((service) => {
              const isExpanded = activeCardId === service.id;
              const isHovered = activeCardId === service.id;

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={service.id}
                  sx={{ height: "90%", width: "90%", position: "relative" }}
                >
                  <StyledCard
                    isExpanded={isExpanded}
                    background={service.background}
                    onMouseEnter={() => handleCardMouseEnter(service.id)}
                    onMouseLeave={handleCardMouseLeave}
                    onClick={() => handleCardClick(service.link)}
                  >
                    {/* Dark Overlay on Hover */}
                    <DarkOverlay
                      className="dark-overlay"
                      isVisible={isHovered}
                    >
                      <DescriptionText
                        className="description-text"
                        isVisible={isHovered}
                      >
                        {service.description}
                      </DescriptionText>
                    </DarkOverlay>

                    <CardContent>
                      <StyledButton
                        isExpanded={isExpanded}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click when button is clicked
                          handleCardClick(service.link);
                        }}
                      >
                        <DomainAddIcon
                          sx={{
                            fontSize: 26,
                            mr: 1,
                            backgroundColor: "#0F758E",
                            color: "white",
                            marginLeft: 1,
                            padding: "2px",
                          }}
                        />
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            mr: 0.5,
                          }}
                        >
                          {service.title}
                        </Typography>
                        <ArrowOutwardIcon
                          sx={{
                            height: "18px",
                            color: "#0F758E",
                          }}
                        />
                      </StyledButton>
                    </CardContent>
                  </StyledCard>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </div>
    </>
  );
};

export default FourColumnHover;